import { useCallback, useEffect, useRef, useState } from "react";
import {
  AddPhotoAlternateOutlined,
  CheckCircleOutlined,
  DeleteOutlineOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import Cropper from "react-easy-crop";

import useStyles from "./styles";
import { getCroppedImg } from "../../pingIcons/cropUtils";

const MAX_SIZE_MB = 5;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Seletor de ícone compacto (upload + recorte circular), reaproveitando a
// lógica de crop do pingIcons/cropUtils. Ao confirmar o recorte, devolve uma
// data URL (base64) via onChange — sem depender de upload real para um
// endpoint de anexos.
export default function IconPicker({
  value,
  onChange,
  cor,
  disabled = false,
  onAdjustingChange,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fileInputRef = useRef(null);

  // Enquanto a imagem escolhida ainda não foi confirmada em "Usar esta
  // imagem", avisa o formulário pai (ItemFormModal) para bloquear o Salvar —
  // evita que o usuário recorte a foto e esqueça de confirmar, salvando o
  // item sem imagem.
  useEffect(() => {
    onAdjustingChange?.(Boolean(imageSrc));
    return () => onAdjustingChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc]);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleFile = (selectedFile) => {
    if (!selectedFile || disabled) return;
    if (!selectedFile.type.startsWith("image/")) return;
    if (selectedFile.size > MAX_SIZE) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result?.toString() || null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleConfirmarRecorte = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const dataUrl = await blobToDataUrl(blob);
      onChange(dataUrl);
      setImageSrc(null);
    } finally {
      setProcessing(false);
    }
  };

  if (imageSrc) {
    return (
      <Box sx={styles.adjustContainer}>
        <Alert severity="warning" icon={false} sx={styles.adjustAlert}>
          Ajuste o enquadramento e clique em <strong>"Usar esta imagem"</strong>{" "}
          para confirmar
        </Alert>
        <Box sx={styles.cropArea}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            restrictPosition={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </Box>
        <Box sx={styles.zoomRow}>
          <ZoomOutOutlined fontSize="small" sx={styles.zoomIcon} />
          <input
            type="range"
            min={0.1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: theme.palette.primary.main }}
          />
          <ZoomInOutlined fontSize="small" sx={styles.zoomIcon} />
        </Box>
        <Box sx={styles.adjustActions}>
          <Button
            size="small"
            onClick={() => setImageSrc(null)}
            disabled={processing}
            sx={styles.adjustCancelButton}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmarRecorte}
            disabled={processing}
            sx={styles.adjustConfirmButton}
            startIcon={
              processing ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CheckCircleOutlined />
              )
            }
          >
            Usar esta imagem
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Avatar
        variant="rounded"
        src={value || undefined}
        sx={{
          ...styles.avatar,
          ...(!value ? { backgroundColor: cor } : {}),
        }}
      >
        <AddPhotoAlternateOutlined />
      </Avatar>

      <Box
        sx={{
          ...styles.dropzone,
          ...(dragActive ? styles.dropzoneActive : {}),
        }}
        onClick={() => !disabled && fileInputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <Typography sx={styles.dropzoneText}>
          {value
            ? "Clique ou arraste para trocar o ícone"
            : "Clique ou arraste um ícone (opcional)"}
        </Typography>
      </Box>

      {value && (
        <IconButton
          size="small"
          onClick={() => onChange(null)}
          disabled={disabled}
          sx={styles.removeButton}
        >
          <DeleteOutlineOutlined fontSize="small" />
        </IconButton>
      )}

      <input
        type="file"
        hidden
        ref={fileInputRef}
        accept="image/png, image/jpeg, image/svg+xml, image/webp"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFile(e.target.files[0]);
          e.target.value = null;
        }}
      />
    </Box>
  );
}
